// Project imports
import skovkortetLogo from "assets/images/branding/skovkortet/skovkortet-logo.png";
import forslerLogo from "assets/images/branding/forsler/forsler-logo.png";
import georgeLogo from "assets/images/branding/george/george-logo.svg";
import forslerIcon from "assets/images/branding/forsler/forsler-logo-icon.png";
import skovkortetIcon from "assets/images/branding/skovkortet/skovkortet-logo-icon.png";
import georgeIcon from "assets/images/branding/george/george-logo-icon.svg";
import { UserOrg, UserProfile } from "types/auth";
import { InternalAppType } from "types/branding";
import { isNative } from "lib/capacitor/capacitor";
import { I18n, lang } from "config/config-general";

// Get app type from build variable (used for native apps to avoid issues with window and document access in index.html)
const internalAppTypeFromBuild = process.env.REACT_APP_INTERNAL_APP_TYPE as
  | InternalAppType
  | undefined;

const TEST_BRAND_OVERRIDE_STORAGE_KEY = "ft.testBrandOverride";
const TEST_BRAND_SWITCHER_ENABLED_STORAGE_KEY = "ft.enableBrandSwitcher";
const TEST_BRAND_OVERRIDE_CHANGED_EVENT = "ft:test-brand-override-changed";

const isInternalAppType = (value: string | null | undefined): value is InternalAppType =>
  value === "forsler" || value === "skovkortet" || value === "george";

/**
 * Test overrides are deliberately unavailable in production. They are available on localhost,
 * explicitly enabled preview builds, and developer-opted-in browsers on dev.forsler.com.
 */
export const isTestBrandOverrideEnabled = () => {
  if (isNative) return false;
  if (process.env.REACT_APP_ENVIRONMENT === "prod") return false;

  const hostname = window.location.hostname.toLowerCase();
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1" || hostname.endsWith(".localhost")) {
    return true;
  }

  if (process.env.REACT_APP_ENABLE_BRAND_SWITCHER === "true") return true;
  if (hostname === "dev.forsler.com") {
    try {
      return window.localStorage.getItem(TEST_BRAND_SWITCHER_ENABLED_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  }
  return false;
};

export const getTestBrandOverride = (): InternalAppType | null => {
  if (!isTestBrandOverrideEnabled()) return null;
  try {
    const value = window.localStorage.getItem(TEST_BRAND_OVERRIDE_STORAGE_KEY);
    return isInternalAppType(value) ? value : null;
  } catch {
    return null;
  }
};

export const setTestBrandOverride = (brand: InternalAppType | null) => {
  if (!isTestBrandOverrideEnabled()) return;
  try {
    if (brand) window.localStorage.setItem(TEST_BRAND_OVERRIDE_STORAGE_KEY, brand);
    else window.localStorage.removeItem(TEST_BRAND_OVERRIDE_STORAGE_KEY);
    window.dispatchEvent(new Event(TEST_BRAND_OVERRIDE_CHANGED_EVENT));
  } catch {
    // Storage can be unavailable in privacy-restricted browsers. The application still works
    // with the normal runtime and organisation brand resolution.
  }
};

export const testBrandOverrideChangedEvent = TEST_BRAND_OVERRIDE_CHANGED_EVENT;

type GetInternalAppType = {
  pathOrHost?: string; // Path or host name
  orgData?: UserOrg | null | undefined;
  userData?: UserProfile | null | undefined;
};
export function getInternalAppType({
  pathOrHost,
  orgData,
  userData,
}: GetInternalAppType): InternalAppType {
  // Native applications are single-brand builds and cannot be changed at runtime.
  if (isNative && internalAppTypeFromBuild) {
    return internalAppTypeFromBuild;
  }

  // Local/preview selection is intentionally runtime-only. It affects UI and newly created
  // users/orgs, but never changes an existing organisation's stored brand or backend emails.
  const testOverride = getTestBrandOverride();
  if (testOverride) return testOverride;

  // A persisted organisation brand is authoritative for organisation-scoped UI.
  if (orgData?.internalAppType) {
    return orgData.internalAppType as InternalAppType;
  }
  if (orgData?.defaultLanguageCode && orgData?.defaultLanguageCode !== "") {
    if (orgData.defaultLanguageCode === "da") {
      return "skovkortet";
    } else {
      return "forsler";
    }
  }
  if (orgData?.countryCode) {
    if (orgData.countryCode === "DK") {
      return "skovkortet";
    } else {
      return "forsler";
    }
  }
  if (userData?.internalAppType) {
    return userData.internalAppType as InternalAppType;
  }
  if (userData?.countryCode) {
    if (userData.countryCode === "DK") {
      return "skovkortet";
    } else {
      return "forsler";
    }
  }

  const hostOrPath = (pathOrHost || window.location.hostname).toLowerCase();
  if (hostOrPath.includes("george")) return "george";
  if (hostOrPath.includes("skovkortet")) return "skovkortet";
  if (hostOrPath.includes("forsler")) return "forsler";
  return "forsler";
}

export const getInternalAppName = (appType: InternalAppType) => {
  switch (appType) {
    case "skovkortet":
      return "Skovkortet.dk";
    case "forsler":
      return "Forsler.com";
    case "george":
      return "George";
    default:
      return "George";
  }
};

export const getBrandingAssetsForInternalAppType = (appType?: InternalAppType) => {
  // Setup default assets to Forsler, so we have something to fall back to
  // if app type is undefined or doesn't match any case
  const defaultAssets = {
    logo: georgeLogo,
    icon: georgeIcon,
    alt: "George logo",
  };
  // Return specific assets based on app type
  switch (appType) {
    case "skovkortet":
      return {
        logo: skovkortetLogo,
        icon: skovkortetIcon,
        alt: "Skovkortet.dk logo",
      };
    case "forsler":
      return {
        logo: forslerLogo,
        icon: forslerIcon,
        alt: "Forsler.com logo",
      };
    case "george":
      return {
        logo: georgeLogo,
        icon: georgeIcon,
        alt: "George logo",
      };
    default:
      return defaultAssets;
  }
};

// Utility function to extract language from URL path
// Example: www.skovkortet.dk/en/some-page => returns "en"
// If no supported language is found in the path, returns undefined
type GetLanguageFromUrlProps = {
  pathname: string;
};

export function getLanguageFromUrl({ pathname }: GetLanguageFromUrlProps): I18n | undefined {
  const supportedLanguages = lang;
  const pathParts = pathname
    .split("/")
    .filter(Boolean)
    .map((part) => part.toLowerCase());

  return pathParts.find((part): part is I18n => supportedLanguages.includes(part as I18n));
}

export function getSpecificParamFromUrlParams(urlParams: URLSearchParams | null, param: string): string | null {
  return urlParams && urlParams.has(param) ? urlParams.get(param)?.trim() ?? null : null;
}
